/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import sanitizeHtml from 'sanitize-html';

const SEO = ({
  description = '',
  title = '',
  location = null,
  noIndex = null,
  children = null,
  data = null,
  pageContext = null,
}) => {
  if (!data) return null;
  if (!pageContext) return null;
  if (!location) return null;

  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
          siteUrl
        }
      }
    }
  `);
  const { pageTitle, metaDescription } = data;
  const displayInSiteMap = noIndex || data.displayInSiteMap;
  const hostname = process.env.BASE_URL.replace(/\/$/, '');
  const path = location ? hostname + location.pathname : '';
  const lang = pageContext.language;
  const otherLang = lang === 'en' ? 'fr' : 'en';
  const otherPath = path
    ? `${path.replace(`/${pageContext.language}/`, `/${otherLang}/`)}`
    : '';
  const rawTitle = title || pageTitle;
  const formattedTitle =
    lang === 'en'
      ? `${rawTitle} | ${site.siteMetadata.title}`
      : `${rawTitle} | PC Finance`;
  const { showFrench = true } = pageContext;

  const seo = {
    title: sanitizeHtml(formattedTitle) || site.siteMetadata.title,
    description:
      description || metaDescription || site.siteMetadata.description,
    url: sanitizeHtml(path),
    twitterUsername: site.siteMetadata.author,
    canonicalPath: sanitizeHtml(path) || process.env.BASE_URL,
    lang: sanitizeHtml(`${lang}-ca`),
    otherLang: sanitizeHtml(`${otherLang}-ca`),
    path: sanitizeHtml(path),
    otherPath: sanitizeHtml(otherPath),
  };

  return (
    <>
      <html lang={lang} />
      <title>{seo.title}</title>
      {showFrench && (
        <link rel="alternate" hrefLang={seo.lang} href={seo.path} />
      )}
      {showFrench && (
        <link rel="alternate" hrefLang={seo.otherLang} href={seo.otherPath} />
      )}
      <link rel="canonical" href={seo.canonicalPath} />
      <meta name="og:title" content={seo.title} />
      <meta name="og:description" content={seo.description} />
      <meta name="og:type" content="website" />
      <meta name="description" content={seo.description} />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:creator" content={seo.twitterUsername} />
      {!displayInSiteMap && <meta name="robots" content="noindex" />}
      {children}
    </>
  );
};

SEO.propTypes = {
  description: PropTypes.string,
  title: PropTypes.string,
  location: PropTypes.shape(),
  data: PropTypes.shape(),
  pageContext: PropTypes.shape(),
  noIndex: PropTypes.bool,
  children: PropTypes.shape(),
};

export default SEO;
